import { useState } from 'react';
import { Database } from "@/integrations/supabase/types";
import i18n from '@/i18n/config';

type Role = Database['public']['Enums']['app_role'];
type OnboardingStep = 'language' | 'role' | 'personal' | 'portfolio' | 'opportunity';

export const useOnboardingState = () => {
  const [currentStep, setCurrentStep] = useState<OnboardingStep>('language');
  const [selectedRole, setSelectedRole] = useState<Role | ''>('');
  const [isLoading, setIsLoading] = useState(false);

  const getNextStep = (currentStep: OnboardingStep): OnboardingStep | null => {
    const talentFlow: OnboardingStep[] = ['language', 'role', 'personal', 'portfolio'];
    const recruiterFlow: OnboardingStep[] = ['language', 'role', 'personal', 'opportunity'];
    
    const flow = selectedRole === 'talent' ? talentFlow : recruiterFlow;
    const currentIndex = flow.indexOf(currentStep);
    
    if (currentIndex === -1 || currentIndex === flow.length - 1) {
      return null;
    }
    
    return flow[currentIndex + 1];
  };

  const getPreviousStep = (currentStep: OnboardingStep): OnboardingStep | null => {
    const talentFlow: OnboardingStep[] = ['language', 'role', 'personal', 'portfolio'];
    const recruiterFlow: OnboardingStep[] = ['language', 'role', 'personal', 'opportunity'];
    
    const flow = selectedRole === 'talent' ? talentFlow : recruiterFlow;
    const currentIndex = flow.indexOf(currentStep);
    
    if (currentIndex <= 0) {
      return null;
    }
    
    return flow[currentIndex - 1];
  };

  const handleLanguageSelect = (language: 'pt' | 'en') => {
    i18n.changeLanguage(language);
    localStorage.setItem('preferredLanguage', language);
    handleStepComplete();
  };

  const handleStepComplete = () => {
    const nextStep = getNextStep(currentStep);
    if (nextStep) {
      setCurrentStep(nextStep);
    }
    return nextStep === null; // Returns true if we're done
  };

  const handleStepBack = () => {
    const previousStep = getPreviousStep(currentStep);
    if (previousStep) {
      setCurrentStep(previousStep);
    }
  };

  return {
    currentStep,
    selectedRole,
    isLoading,
    setSelectedRole,
    setIsLoading,
    handleStepComplete,
    handleStepBack,
    handleLanguageSelect,
  };
};